import React, { Component } from "react";
import { Select, Table, Button, Modal, message, Input } from "antd";
import { EditOutlined } from "@ant-design/icons";
import jwtDecode from "jwt-decode";
import moment from "moment";
import Loader from "../loader";
import Fetch from "../../utils/fetch";
import CrewDetails from "../crewDetails";
import OpsTicketSearch from "../opsTicketSearch";

const { Option } = Select;
const wordBreakStyle = { wordBreak: "break-word" };
const detailRowStyle = { display: "flex", gap: "10px", marginBottom: "8px" };
const detailLabelStyle = { minWidth: "140px", fontWeight: 500 };

// Ops ticket statuses and the colour used for the status pill in the table.
const STATUS_COLORS = {
  CREATED: { color: "#595959", background: "#f0f0f0", border: "#d9d9d9" },
  INPROGRESS: { color: "#ad6800", background: "#fff7e6", border: "#ffd591" },
  CLOSED: { color: "#237804", background: "#f6ffed", border: "#b7eb8f" },
  REJECTED: { color: "#a8071a", background: "#fff1f0", border: "#ffa39e" }
};

const columns = [
  {
    title: "Service Name",
    dataIndex: "serviceName",
    key: "serviceName",
    width: "12%"
  },
  {
    title: "Trip Date",
    dataIndex: "date",
    key: "date",
    width: "12%"
  },
  {
    title: "Complaint Category",
    dataIndex: "complainCategory",
    key: "complainCategory",
    width: "11%"
  },
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
    width: "16%",
    render: value => <span style={wordBreakStyle}>{value}</span>
  },
  {
    title: "Bus RC",
    dataIndex: "busRc",
    key: "busRc",
    width: "8%"
  },
  {
    title: "Operator Name",
    dataIndex: "operatorName",
    key: "operatorName",
    width: "9%"
  },
  {
    title: "Crew Details",
    dataIndex: "crewDetails",
    key: "crewDetails",
    width: "8%",
    render: ({ showModal }) => (
      <Button size="small" onClick={showModal}>
        Crew Details
      </Button>
    )
  },
  {
    title: "Details",
    dataIndex: "details",
    key: "details",
    width: "8%",
    render: ({ showModal }) => (
      <Button size="small" onClick={showModal}>
        Show Details
      </Button>
    )
  },
  {
    title: "Created On",
    dataIndex: "createdOn",
    key: "createdOn",
    width: "10%"
  },
  {
    title: "Remarks",
    dataIndex: "remarks",
    key: "remarks",
    width: "9%",
    render: ({
      value,
      _id,
      allRemarks,
      remarksUpdate,
      serviceId,
      disabled
    }) => (
      <div>
        {value ? (
          <span style={wordBreakStyle}>{value} </span>
        ) : (
          <span style={{ color: "#8c8cf1" }}>Add </span>
        )}
        {disabled ? null : (
          <EditOutlined
            style={{ color: "#722ed1", cursor: "pointer" }}
            onClick={() => remarksUpdate({ allRemarks, value, _id, serviceId })}
          />
        )}
      </div>
    )
  },
  {
    title: "Department",
    dataIndex: "department",
    key: "department",
    width: "8%",
    render: ({ serviceId, department, _id, updateDepartment, disabled }) => (
      <Select
        value={department || "Ops Team"}
        disabled={disabled}
        style={{ width: 120 }}
        onChange={value =>
          updateDepartment({ serviceId, department: value, _id })
        }
      >
        <Option value="Ops Team">Ops Team</Option>
      </Select>
    )
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: "10%",
    render: ({ status, _id, updateStatus, serviceId, disabled }) => {
      const palette = STATUS_COLORS[status] || STATUS_COLORS.CREATED;
      return (
        <Select
          value={status}
          disabled={disabled}
          style={{
            width: 130,
            color: palette.color,
            fontWeight: 500
          }}
          onChange={value => updateStatus({ status: value, _id, serviceId })}
          dropdownStyle={{ minWidth: 130 }}
          getPopupContainer={trigger => trigger.parentNode}
        >
          {["CREATED", "INPROGRESS", "CLOSED", "REJECTED"].map(option => {
            const optionPalette = STATUS_COLORS[option];
            return (
              <Option value={option} key={option}>
                <span
                  style={{
                    color: optionPalette.color,
                    fontWeight: 500
                  }}
                >
                  {option}
                </span>
              </Option>
            );
          })}
        </Select>
      );
    }
  },
  {
    title: "Logs",
    dataIndex: "logs",
    key: "logs",
    width: "6%",
    render: ({ showModal }) => (
      <Button size="small" type="primary" ghost onClick={showModal}>
        Logs
      </Button>
    )
  }
];

class opsTicketView extends Component {
  state = {
    loading: true,
    contentLoading: false,
    opsResponse: [],
    searchedData: {},
    // remarks edit modal
    showRemarksEdit: false,
    remarksUpdateObj: {},
    remarkInput: "",
    // crew details modal
    showCrewDetails: false,
    crewDetails: null,
    // ticket details modal
    showDetails: false,
    detailsData: null,
    // logs modal
    showLogs: false,
    logsData: [],
    // status -> CLOSED remarks modal
    showCloseRemarks: false,
    closeStatusObj: {},
    closeRemarkInput: ""
  };

  async componentDidMount() {
    try {
      const opsResponse = await this.fetchOpsTickets();
      this.setState({ opsResponse: opsResponse || [], loading: false });
    } catch (err) {
      console.log(err);
      this.setState({ loading: false });
    }
  }

  fetchOpsTickets = async (data = {}) => {
    try {
      const url = `/complaints/opsTicketSearch`;
      const options = { method: "post", data };
      const response = await Fetch(url, options);
      if (response && Array.isArray(response)) {
        return this.formattedData(response);
      }
      return [];
    } catch (err) {
      console.log(err);
      message.error(err);
      return [];
    }
  };

  // Maps the raw ticket list into table rows and stacks multiple violations of
  // the same trip under the first row (matching the crew tickets screen).
  formattedData = response => {
    try {
      const hashMap = {};
      const formatted = [];

      for (let i = 0; i < response.length; i++) {
        const ticket = response[i];

        let remarksList = ticket.remarks ? ticket.remarks.split("|") : [];
        remarksList = remarksList.filter(item => item !== "");
        const latestRemark = remarksList[remarksList.length - 1] || "";

        const isClosed = ticket.status === "CLOSED";
        const date = this.parseDwellTime(ticket.date);
        const row = {
          key: ticket._id,
          _id: ticket._id,
          serviceId: ticket.serviceId,
          serviceName: ticket.serviceName,
          date: date.isValid() ? date.format("LL") : "-",
          complainCategory: ticket.complaintCategory || ticket.complainCategory,
          description: ticket.description || ticket.comment,
          busRc: ticket.busRc || "-",
          operatorName: ticket.operatorName || "-",
          createdOn: ticket.createdOn
            ? moment(ticket.createdOn).format("LLL")
            : "-",
          crewDetails: {
            showModal: () =>
              this.setState({
                showCrewDetails: true,
                crewDetails: ticket.crewDetails || ticket.trip || null
              })
          },
          details: {
            showModal: () =>
              this.setState({
                showDetails: true,
                detailsData: this.buildTicketDetails(ticket)
              })
          },
          remarks: {
            _id: ticket._id,
            serviceId: ticket.serviceId,
            value: latestRemark,
            allRemarks: ticket.remarks,
            remarksUpdate: this.remarksUpdate,
            disabled: isClosed
          },
          department: {
            _id: ticket._id,
            serviceId: ticket.serviceId,
            department: ticket.department,
            updateDepartment: this.updateDepartment,
            disabled: isClosed
          },
          status: {
            _id: ticket._id,
            serviceId: ticket.serviceId,
            status: ticket.status,
            updateStatus: this.updateStatus,
            // Once closed, the status can no longer be changed (TRAC-683).
            disabled: isClosed
          },
          logs: {
            showModal: () => this.showLogsModal(ticket._id)
          }
        };

        const tripKey = `${ticket.tripId || ticket._id}`;
        if (hashMap[tripKey]) {
          hashMap[tripKey].push(row);
        } else {
          hashMap[tripKey] = [row];
        }
        formatted.push(row);
      }

      const grouped = [];
      for (const tripKey in hashMap) {
        const rows = hashMap[tripKey];
        for (let z = 1; z < rows.length; z++) {
          if (rows[0].children) {
            rows[0].children.push(rows[z]);
          } else {
            rows[0].serviceName = `${rows[0].serviceName} (${rows.length} Issue Raised)`;
            rows[0].children = [];
            rows[0].children.push(rows[z]);
          }
        }
        grouped.push(rows[0]);
      }

      return grouped;
    } catch (error) {
      console.log(error);
      return [];
    }
  };

  // Dwell timestamps arrive as epoch millis or an ISO string. Returns an
  // invalid moment when the value is missing or unparseable.
  parseDwellTime = value =>
    value ? moment(isNaN(value) ? value : Number(value)) : moment(null);

  // Trip / bus / crew summary plus the location the alert was raised at. Only
  // unauthorised stop tickets carry lat & lng; the rest stop violations
  // (short / excessive wait) carry the dwell window instead, which is shown as
  // "Bus stopped for X mins from <start> to <end>". Falls back to the comment
  // sent by the backend when the window is missing.
  buildTicketDetails = ticket => {
    const crew = ticket.crewDetails || ticket.trip || {};
    const busDriver = crew.busDriver || null;
    const busHost = crew.busHost || null;
    const hasLatLng = ticket.lat != null && ticket.lng != null;

    const start = this.parseDwellTime(ticket.startTime);
    const end = this.parseDwellTime(ticket.endTime);
    const hasDwell = start.isValid() && end.isValid();
    let durationMinutes = ticket.durationMinutes;
    if (hasDwell && durationMinutes == null) {
      durationMinutes = Math.round(end.diff(start, "minutes", true));
    }

    return {
      tripId: ticket.tripId || "-",
      busRc: ticket.busRc || "-",
      busDriver: busDriver
        ? `${busDriver.name || "-"}${
            busDriver.mobileNo ? ` (${busDriver.mobileNo})` : ""
          }`
        : "-",
      busHost: busHost
        ? `${busHost.name || "-"}${
            busHost.mobileNo ? ` (${busHost.mobileNo})` : ""
          }`
        : "-",
      hasLatLng,
      lat: ticket.lat,
      lng: ticket.lng,
      hasDwell,
      durationMinutes,
      startTime: hasDwell ? start.format("DD MMM, hh:mm A") : "",
      endTime: hasDwell ? end.format("DD MMM, hh:mm A") : "",
      mapLink: hasLatLng
        ? `https://www.google.com/maps?q=${ticket.lng},${ticket.lat}`
        : ""
    };
  };

  showLoading = () => {
    this.setState({ contentLoading: true });
  };

  // Called by the search bar. Fetches filtered tickets.
  updateData = async data => {
    try {
      const formatted = await this.fetchOpsTickets(data);
      this.setState({
        searchedData: data,
        opsResponse: formatted,
        contentLoading: false
      });
    } catch (err) {
      console.log(err);
      this.setState({ contentLoading: false });
      message.error(err);
    }
  };

  // Locates a ticket row (top level or nested child) by _id.
  findRow = _id => {
    const { opsResponse } = this.state;
    for (const row of opsResponse) {
      if (row._id === _id) return row;
      if (row.children) {
        const child = row.children.find(item => item._id === _id);
        if (child) return child;
      }
    }
    return null;
  };

  // Builds a new pipe-delimited remark stamped with the current user + time.
  buildRemark = (existing, text) => {
    const token = localStorage.getItem("idToken");
    const decodedToken = token ? jwtDecode(token) : {};
    const prefix = existing ? `${existing} ` : "";
    return `${prefix}${decodedToken.name || "user"},${moment().format(
      "LLL"
    )}: ${text} |`;
  };

  patchTicket = async payload => {
    const url = `/complaints/opsTicketUpdate`;
    const options = { method: "patch", data: payload };
    const response = await Fetch(url, options);
    if (response && response._id) {
      // Server returns only the changed fields; patch that row in place so we
      // avoid re-fetching the whole list. A reload reflects the same values.
      this.applyTicketUpdate(response);
      message.success("Data is updated");
      return true;
    }
    message.error("Data not updated");
    return false;
  };

  // Applies the changed fields returned by opsTicketUpdate to the matching row
  // (top level or nested child) without a network round-trip.
  applyTicketUpdate = updated => {
    const { _id } = updated;

    const patchRow = row => {
      if (row._id !== _id) return false;
      const closing = updated.status === "CLOSED";
      if (updated.status) {
        row.status = { ...row.status, status: updated.status };
      }
      if (updated.department) {
        row.department = { ...row.department, department: updated.department };
      }
      if (updated.remarks !== undefined) {
        let remarksList = updated.remarks ? updated.remarks.split("|") : [];
        remarksList = remarksList.filter(item => item !== "");
        row.remarks = {
          ...row.remarks,
          value: remarksList[remarksList.length - 1] || "",
          allRemarks: updated.remarks
        };
      }
      // Once closed, no field on the row can be edited any more (TRAC-683).
      if (closing) {
        row.status = { ...row.status, disabled: true };
        row.department = { ...row.department, disabled: true };
        row.remarks = { ...row.remarks, disabled: true };
      }
      return true;
    };

    const opsResponse = this.state.opsResponse.map(row => {
      if (patchRow(row)) return { ...row };
      if (row.children) {
        row.children = row.children.map(child =>
          patchRow(child) ? { ...child } : child
        );
      }
      return { ...row };
    });

    this.setState({ opsResponse });
  };

  remarksUpdate = obj => {
    let remarksArray = obj.allRemarks ? obj.allRemarks.split("|") : [];
    remarksArray = remarksArray.filter(item => item !== "");
    this.setState({
      showRemarksEdit: true,
      remarkInput: "",
      remarksUpdateObj: {
        allRemarks: remarksArray,
        rawRemarks: obj.allRemarks,
        _id: obj._id,
        serviceId: obj.serviceId
      }
    });
  };

  updateRemark = async () => {
    const { remarkInput, remarksUpdateObj } = this.state;
    if (!remarkInput.trim()) {
      message.error("please enter something");
      return;
    }
    const remark = this.buildRemark(remarksUpdateObj.rawRemarks, remarkInput);
    const ok = await this.patchTicket({
      _id: remarksUpdateObj._id,
      remark
    });
    if (ok) {
      this.setState({
        showRemarksEdit: false,
        remarkInput: "",
        remarksUpdateObj: {}
      });
    }
  };

  updateDepartment = async obj => {
    const row = this.findRow(obj._id);
    const remark = this.buildRemark(
      row && row.remarks ? row.remarks.allRemarks : "",
      `department moved to ${obj.department}`
    );
    await this.patchTicket({
      _id: obj._id,
      department: obj.department,
      remark
    });
  };

  updateStatus = async obj => {
    // Closing a ticket opens an (optional) remarks popup first.
    if (obj.status === "CLOSED") {
      this.setState({
        showCloseRemarks: true,
        closeRemarkInput: "",
        closeStatusObj: obj
      });
      return;
    }
    const row = this.findRow(obj._id);
    const fromStatus = row && row.status ? row.status.status : "";
    const remark = this.buildRemark(
      row && row.remarks ? row.remarks.allRemarks : "",
      `moved from ${fromStatus} to ${obj.status}`
    );
    await this.patchTicket({
      _id: obj._id,
      status: obj.status,
      remark
    });
  };

  // Confirm closing a ticket. The optional remark also lands in the Remarks
  // history (TRAC-683).
  confirmClose = async () => {
    const { closeStatusObj, closeRemarkInput } = this.state;
    const row = this.findRow(closeStatusObj._id);
    const fromStatus = row && row.status ? row.status.status : "";
    const note = closeRemarkInput.trim()
      ? `moved from ${fromStatus} to CLOSED - ${closeRemarkInput.trim()}`
      : `moved from ${fromStatus} to CLOSED`;
    const remark = this.buildRemark(
      row && row.remarks ? row.remarks.allRemarks : "",
      note
    );
    const ok = await this.patchTicket({
      _id: closeStatusObj._id,
      status: "CLOSED",
      remark
    });
    if (ok) {
      this.setState({
        showCloseRemarks: false,
        closeRemarkInput: "",
        closeStatusObj: {}
      });
    }
  };

  // Each remark is stored as `<actor>,<timestamp>: <message>` (see buildRemark).
  // Split it back into its parts for display; anything that doesn't match the
  // pattern is shown as-is under the message column.
  parseLogEntry = entry => {
    const trimmed = entry.trim();
    const firstComma = trimmed.indexOf(",");
    const sepIdx = trimmed.indexOf(": ");
    if (firstComma !== -1 && sepIdx !== -1 && firstComma < sepIdx) {
      return {
        actor: trimmed.slice(0, firstComma).trim(),
        timestamp: trimmed.slice(firstComma + 1, sepIdx).trim(),
        message: trimmed.slice(sepIdx + 2).trim()
      };
    }
    return { actor: "-", timestamp: "-", message: trimmed };
  };

  showLogsModal = _id => {
    // Read the row's live remarks from state so logs reflect updates applied
    // locally after a status/remark change (no re-fetch).
    const row = this.findRow(_id);
    const rawRemarks = row && row.remarks ? row.remarks.allRemarks : "";
    let logsList = rawRemarks ? rawRemarks.split("|") : [];
    logsList = logsList
      .filter(item => item !== "" && item.trim() !== "")
      .map((entry, index) => ({ key: index, ...this.parseLogEntry(entry) }));
    this.setState({ showLogs: true, logsData: logsList });
  };

  render() {
    const {
      loading,
      contentLoading,
      opsResponse,
      showRemarksEdit,
      remarksUpdateObj,
      remarkInput,
      showCrewDetails,
      crewDetails,
      showDetails,
      detailsData,
      showLogs,
      logsData,
      showCloseRemarks,
      closeRemarkInput
    } = this.state;

    if (loading) {
      return <Loader />;
    }

    const logColumns = [
      {
        title: "#",
        dataIndex: "key",
        key: "key",
        width: "6%",
        render: value => value + 1
      },
      {
        title: "Actor",
        dataIndex: "actor",
        key: "actor",
        width: "18%",
        render: value => <span style={wordBreakStyle}>{value}</span>
      },
      {
        title: "Timestamp",
        dataIndex: "timestamp",
        key: "timestamp",
        width: "22%"
      },
      {
        title: "Log",
        dataIndex: "message",
        key: "message",
        render: value => <span style={wordBreakStyle}>{value}</span>
      }
    ];

    return (
      <div>
        <OpsTicketSearch
          showLoader={this.showLoading}
          onSearch={this.updateData}
        />
        <div>
          <Table
            scroll={{ y: "70vh", x: "130%" }}
            columns={columns}
            loading={contentLoading}
            pagination={false}
            dataSource={opsResponse}
          />
        </div>

        {showCrewDetails ? (
          <CrewDetails
            details={crewDetails || {}}
            onCancel={() =>
              this.setState({ showCrewDetails: false, crewDetails: null })
            }
          />
        ) : null}

        <Modal
          title="Details"
          visible={showDetails}
          footer={null}
          onCancel={() =>
            this.setState({ showDetails: false, detailsData: null })
          }
        >
          {detailsData ? (
            <>
              <div style={detailRowStyle}>
                <span style={detailLabelStyle}>Trip Id:</span>
                <span style={wordBreakStyle}>{detailsData.tripId}</span>
              </div>
              <div style={detailRowStyle}>
                <span style={detailLabelStyle}>Bus RC:</span>
                <span style={wordBreakStyle}>{detailsData.busRc}</span>
              </div>
              <div style={detailRowStyle}>
                <span style={detailLabelStyle}>Bus Driver:</span>
                <span style={wordBreakStyle}>{detailsData.busDriver}</span>
              </div>
              <div style={detailRowStyle}>
                <span style={detailLabelStyle}>Bus Host:</span>
                <span style={wordBreakStyle}>{detailsData.busHost}</span>
              </div>
              {detailsData.hasLatLng ? (
                <>
                  <div style={detailRowStyle}>
                    <span style={detailLabelStyle}>Lat:</span>
                    <span style={wordBreakStyle}>{detailsData.lng}</span>
                  </div>
                  <div style={detailRowStyle}>
                    <span style={detailLabelStyle}>Lng:</span>
                    <span style={wordBreakStyle}>{detailsData.lat}</span>
                  </div>
                  <a
                    href={detailsData.mapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View on map
                  </a>
                </>
              ) : (
                <div style={detailRowStyle}>
                  <span style={detailLabelStyle}>Comment:</span>
                  <span style={wordBreakStyle}>
                    {detailsData.hasDwell ? (
                      <>
                        Bus stopped for{" "}
                        <b>{detailsData.durationMinutes} mins</b> from{" "}
                        <b>{detailsData.startTime}</b> to{" "}
                        <b>{detailsData.endTime}</b>
                      </>
                    ) : null}
                  </span>
                </div>
              )}
            </>
          ) : (
            "No details found"
          )}
        </Modal>

        <Modal
          title="Logs"
          visible={showLogs}
          centered
          width={"100%"}
          footer={null}
          onCancel={() => this.setState({ showLogs: false, logsData: [] })}
        >
          <Table
            columns={logColumns}
            dataSource={logsData}
            pagination={false}
            locale={{ emptyText: "No logs found" }}
          />
        </Modal>

        <Modal
          title="Remarks"
          visible={showRemarksEdit}
          okText="Update"
          onOk={this.updateRemark}
          onCancel={() =>
            this.setState({ showRemarksEdit: false, remarkInput: "" })
          }
        >
          {Array.isArray(remarksUpdateObj.allRemarks)
            ? remarksUpdateObj.allRemarks.map((text, index) => (
                <div key={index}>
                  <p>{text}</p>
                </div>
              ))
            : null}
          <Input
            value={remarkInput}
            placeholder="Enter Remark"
            onChange={e => this.setState({ remarkInput: e.target.value })}
          />
        </Modal>

        <Modal
          title="Close Ticket - Remarks (optional)"
          visible={showCloseRemarks}
          okText="Close Ticket"
          onOk={this.confirmClose}
          onCancel={() =>
            this.setState({
              showCloseRemarks: false,
              closeRemarkInput: "",
              closeStatusObj: {}
            })
          }
        >
          <p>
            Once a ticket is closed its status can no longer be changed. Adding
            a remark is optional.
          </p>
          <Input.TextArea
            rows={3}
            value={closeRemarkInput}
            placeholder="Enter remark (optional)"
            onChange={e => this.setState({ closeRemarkInput: e.target.value })}
          />
        </Modal>
      </div>
    );
  }
}

export default opsTicketView;
